import styled, { css, type DefaultTheme } from "styled-components";
import { ScrollableTableViewport } from "../../molecules/ScrollableTableViewport";
import {
  fontWeights,
  typographyMixin,
} from "../../../design-system/tokens/typography";
import { rawColors } from "../../../design-system/tokens/colors";
import type { DataStudioColumnQualityStatus } from "../../../services/dataStudio/dataStudioTables.types";

/**
 * Single source of truth for the excellent/good/fair/poor -> green/yellow/
 * orange/red mapping so the column header strip and the table-level quality
 * score never drift out of sync again. "good" and "poor" pull the true
 * yellow/red from rawColors.accent since theme.colors has no yellow scale
 * and its error scale is pink, not red.
 */
const qualityStatusColor = (
  theme: DefaultTheme,
  status?: DataStudioColumnQualityStatus,
): string => {
  switch (status) {
    case "excellent":
      return theme.colors.success[500];
    case "good":
      return rawColors.accent.yellow500;
    case "fair":
      return theme.colors.warning[500];
    case "poor":
      return rawColors.accent.red500;
    default:
      return theme.colors.border.stroke;
  }
};

const cellEdges = css`
  border-right: ${({ theme }) => theme.borders.width[1]}
    ${({ theme }) => theme.borders.style.solid}
    ${({ theme }) => theme.colors.border.stroke};
  border-bottom: ${({ theme }) => theme.borders.width[1]}
    ${({ theme }) => theme.borders.style.solid}
    ${({ theme }) => theme.colors.border.stroke};
`;

export const ProfileLayout = styled.div`
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  flex-direction: column;
  box-sizing: border-box;
  overflow: hidden;
`;

export const ProfileContent = styled.div`
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.base[3]};
`;

export const ProfileCard = styled.section`
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing.base[3]};
  box-sizing: border-box;
  overflow: hidden;
  border: ${({ theme }) => theme.borders.width[1]}
    ${({ theme }) => theme.borders.style.solid}
    ${({ theme }) => theme.colors.border.stroke};
  border-radius: ${({ theme }) => theme.borders.radius.xl};
  background: ${({ theme }) => theme.colors.background.primary};
`;

export const ProfileHeader = styled.header`
  display: flex;
  flex: 0 0 ${({ theme }) => theme.spacing.base[14]};
  min-width: 0;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.base[4]};
  padding-bottom: ${({ theme }) => theme.spacing.base[2]};
  box-sizing: border-box;
`;

export const TableIdentity = styled.div`
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.base[0.5]};
`;

export const TableTitleRow = styled.div`
  display: flex;
  min-width: 0;
  height: ${({ theme }) => theme.spacing.base[6]};
  align-items: center;
  gap: ${({ theme }) => theme.spacing.base[3]};
`;

export const TableTitleIcon = styled.span`
  display: inline-flex;
  flex: 0 0 ${({ theme }) => theme.spacing.base[4]};
  width: ${({ theme }) => theme.spacing.base[4]};
  height: ${({ theme }) => theme.spacing.base[4]};
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.text.interactive};

  img,
  svg {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

export const TableTitle = styled.h1`
  min-width: 0;
  margin: 0;
  overflow: hidden;
  ${typographyMixin("subtitle", 2)}
  line-height: ${({ theme }) => theme.typography.heading.h3.lineHeight};
  color: ${({ theme }) => theme.colors.text.primary};
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const TableMetadata = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.base[2]};
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const MetadataPill = styled.span`
  flex: 0 0 auto;
  padding: ${({ theme }) => theme.spacing.base[1]}
    calc(
      ${({ theme }) => theme.spacing.base[2]} +
        ${({ theme }) => theme.spacing.base[0.5]}
    );
  border-radius: ${({ theme }) => theme.borders.radius.full};
  background: ${({ theme }) => theme.colors.background.tertiary};
  ${typographyMixin("caption", 4)}
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: nowrap;
`;

export const QualitySummary = styled.p`
  flex: 0 0 auto;
  margin: 0;
  ${typographyMixin("body", 4)}
  color: ${({ theme }) => theme.colors.text.secondary};
  white-space: nowrap;
`;

export const QualityScore = styled.strong<{
  $qualityStatus?: DataStudioColumnQualityStatus;
}>`
  color: ${({ $qualityStatus, theme }) =>
    qualityStatusColor(theme, $qualityStatus)};
  font-weight: ${fontWeights.bold};
`;

export const ProfileScroller = styled.div`
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  overflow-x: auto;
  overflow-y: hidden;
  /* Lets sticky banners size to the visible width, not the wide stage. */
  container-type: inline-size;
  border: ${({ theme }) => theme.borders.width[1]}
    ${({ theme }) => theme.borders.style.solid}
    ${({ theme }) => theme.colors.border.stroke};
  border-radius: ${({ theme }) => theme.borders.radius.lg};
  scrollbar-color: ${({ theme }) => theme.colors.grey[500]} transparent;
  scrollbar-width: thin;
`;

export const ProfileTableStage = styled.div<{ $columnCount: number }>`
  display: flex;
  flex: 0 0 auto;
  width: calc(
    ${({ theme }) => theme.spacing.base[60]} *
      ${({ $columnCount }) => Math.max($columnCount, 1)}
  );
  min-height: 0;
  flex-direction: column;
`;

export const ProfileRowsViewport = styled(ScrollableTableViewport)`
  flex: 1 1 auto;
  min-height: 0;
`;

export const ProfileTable = styled.table`
  && {
    width: 100%;
    min-width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    table-layout: fixed;
  }

  tbody tr:last-child td {
    border-bottom: 0;
  }
`;

export const ColumnHeadingCell = styled.th<{
  /** `undefined` when the column has no `data_quality_score` yet. */
  $qualityStatus?: DataStudioColumnQualityStatus;
}>`
  ${cellEdges}
  position: relative;
  width: ${({ theme }) => theme.spacing.base[60]};
  min-width: ${({ theme }) => theme.spacing.base[60]};
  height: calc(
    ${({ theme }) => theme.spacing.base[14]} -
      ${({ theme }) => theme.spacing.base[0.5]}
  );
  padding: calc(
      ${({ theme }) => theme.spacing.base[4]} +
        ${({ theme }) => theme.spacing.base[0.5]}
    )
    ${({ theme }) => theme.spacing.base[5]}
    ${({ theme }) => theme.spacing.base[3]};
  box-sizing: border-box;
  background: ${({ theme }) => theme.colors.background.tertiary};
  text-align: left;
  vertical-align: middle;

  &::before {
    content: "";
    position: absolute;
    inset: 0 calc(-1 * ${({ theme }) => theme.borders.width[1]}) auto 0;
    height: calc(
      ${({ theme }) => theme.spacing.base[2]} -
        ${({ theme }) => theme.borders.width[2]}
    );
    /*
     * An unscored column gets the neutral border colour, not the success
     * green: the API sends a null data_quality_score when a column has not
     * been scored, and painting that excellent would assert a quality the
     * backend never reported.
     */
    background: ${({ $qualityStatus, theme }) =>
      qualityStatusColor(theme, $qualityStatus)};
  }
`;

export const ColumnHeading = styled.div`
  display: flex;
  min-width: 0;
  height: ${({ theme }) => theme.spacing.base[6]};
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.base[2]};
`;

export const ColumnIdentity = styled.div`
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.base[1]};
`;

export const ColumnKindIcon = styled.span`
  display: inline-flex;
  flex: 0 0 ${({ theme }) => theme.spacing.base[6]};
  width: ${({ theme }) => theme.spacing.base[6]};
  height: ${({ theme }) => theme.spacing.base[6]};
  align-items: center;
  justify-content: center;
  border-radius: calc(
    ${({ theme }) => theme.borders.radius.md} +
      ${({ theme }) => theme.borders.width[1]}
  );
  background: ${({ theme }) => theme.colors.background.primary};
  color: ${({ theme }) => theme.colors.text.interactive};

  svg {
    width: ${({ theme }) => theme.spacing.base[3]};
    height: ${({ theme }) => theme.spacing.base[3]};
    stroke-width: 1.8;
  }
`;

export const ColumnName = styled.span`
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  ${typographyMixin("caption", 3)}
  color: ${({ theme }) => theme.colors.text.primary};
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ImportantStar = styled.span`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.warning[500]};

  svg {
    width: ${({ theme }) => theme.spacing.base[4]};
    height: ${({ theme }) => theme.spacing.base[4]};
    fill: currentColor;
  }
`;

export const ColumnActions = styled.span`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.base[2]};
`;

export const ColumnAction = styled.button`
  display: inline-flex;
  width: ${({ theme }) => theme.spacing.base[6]};
  height: ${({ theme }) => theme.spacing.base[6]};
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: calc(
    ${({ theme }) => theme.borders.radius.md} +
      ${({ theme }) => theme.borders.width[1]}
  );
  background: ${({ theme }) => theme.colors.background.primary};
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;

  svg {
    width: ${({ theme }) => theme.spacing.base[3]};
    height: ${({ theme }) => theme.spacing.base[3]};
  }

  &:hover {
    color: ${({ theme }) => theme.colors.text.interactive};
  }

  &:focus-visible {
    outline: ${({ theme }) => theme.borders.width[2]}
      ${({ theme }) => theme.borders.style.solid}
      ${({ theme }) => theme.colors.primary[500]};
    outline-offset: ${({ theme }) => theme.borders.width[1]};
  }
`;

export const StatisticsCell = styled.td<{ $compact?: boolean }>`
  ${cellEdges}
  height: ${({ theme, $compact }) =>
    $compact
      ? "auto"
      : `calc(${theme.spacing.base[24]} + ${theme.spacing.base[0.5]})`};
  padding: ${({ theme, $compact }) =>
      $compact ? theme.spacing.base[1] : theme.spacing.base[3]}
    ${({ theme }) => theme.spacing.base[5]};
  box-sizing: border-box;
  vertical-align: middle;
`;

export const StatisticsGrid = styled.div`
  display: flex;
  width: calc(
    ${({ theme }) => theme.spacing.base[48]} +
      ${({ theme }) => theme.spacing.base[2]}
  );
  max-width: 100%;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.base[2]};
`;

export const StatisticsRow = styled.div`
  display: flex;
  min-width: 0;
  width: 100%;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.base[5]};
`;

/**
 * Keep a trailing metric aligned with the first pair above it, including
 * its right-aligned value (90px per pair in the Figma statistics block).
 */
export const Statistic = styled.span<{ $fill?: boolean }>`
  display: flex;
  flex: ${({ $fill = true }) => ($fill ? "1 1 0" : "0 0 auto")};
  ${({ $fill = true, theme }) =>
    !$fill &&
    css`
    width: calc((100% - ${theme.spacing.base[5]}) / 2);
  `}
  min-width: 0;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.base[2]};
  white-space: nowrap;
`;

export const StatisticLabel = styled.span`
  ${typographyMixin("overline", 4)}
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const StatisticValue = styled.span`
  flex: 0 0 auto;
  ${typographyMixin("overline", 4)}
  font-weight: ${fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

/** `$compact`: no column has a distribution to draw, so the band is one short line. */
export const DistributionCell = styled.td<{ $compact?: boolean }>`
  ${cellEdges}
  height: ${({ theme, $compact }) =>
    $compact ? theme.spacing.base[8] : theme.spacing.base[40]};
  padding: ${({ theme, $compact }) =>
      $compact ? theme.spacing.base[1] : theme.spacing.base[4]}
    ${({ theme }) => theme.spacing.base[5]};
  box-sizing: border-box;
  vertical-align: middle;
`;

export const DataCell = styled.td<{ $numeric: boolean }>`
  ${cellEdges}
  height: ${({ theme }) => theme.spacing.base[8]};
  padding: calc(
      ${({ theme }) => theme.spacing.base[2]} -
        ${({ theme }) => theme.borders.width[1]}
    )
    ${({ theme }) => theme.spacing.base[5]};
  box-sizing: border-box;
  ${typographyMixin("body", 3)}
  color: ${({ theme }) => theme.colors.text.primary};
  text-align: ${({ $numeric }) => ($numeric ? "right" : "left")};
  white-space: nowrap;
`;

export const RowFeedback = styled.div<{ $variant: "info" | "error" }>`
  display: flex;
  /* Anchored to the visible area so the message is never clipped by the
     wide horizontally-scrolling stage. */
  position: sticky;
  left: 0;
  box-sizing: border-box;
  width: 100%;
  width: 100cqw;
  max-width: 100%;
  flex-shrink: 0;
  min-height: fit-content;
  padding-bottom: ${({ theme }) => theme.spacing.base[2]};
  line-height: 1.4;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.base[2]};
  padding-top: ${({ theme }) => theme.spacing.base[3]}
    ${({ theme }) => theme.spacing.base[3]} 0;
  border-top: ${({ theme }) => theme.borders.width[1]}
    ${({ theme }) => theme.borders.style.solid}
    ${({ theme }) => theme.colors.border.stroke};
  ${typographyMixin("caption", 3)}
  color: ${({ $variant, theme }) =>
    $variant === "error"
      ? theme.colors.error.text
      : theme.colors.text.secondary};
`;

export const RowLoadStatus = styled.span`
  position: absolute;
  width: ${({ theme }) => theme.borders.width[1]};
  height: ${({ theme }) => theme.borders.width[1]};
  padding: 0;
  margin: calc(-1 * ${({ theme }) => theme.borders.width[1]});
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
`;
